import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, MessageSquarePlus, Pencil, RefreshCw, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type { EmailDetail } from "@/lib/types";
import { EmailStatusBadge } from "@/components/EmailStatusBadge";
import EmailFeedbackDialog from "@/components/EmailFeedbackDialog";
import EmailBodyView from "@/components/EmailBodyView";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useEmailMode } from "@/components/EmailModeBanner";
import SaveAsExampleControl from "@/components/SaveAsExampleControl";
import { useAuth } from "@/lib/auth";
import type { PhaseExampleSummary } from "@/lib/types";

/** Statuses in which the prospect has actually received an outbound message. */
const RECEIVED: ReadonlySet<string> = new Set(["QUEUED", "SENT", "DELIVERED", "OPENED", "REPLIED", "COMPLAINED"]);

/**
 * Reads one message and, when it is awaiting approval, lets an operator edit,
 * release or decline it.
 *
 * <p>Used from both the Emails oversight list and the prospect detail page, so
 * the review controls behave identically wherever an operator meets a pending
 * message. Feedback capture is offered on <em>every</em> message, sent or not —
 * the most useful training signal usually comes from reading what already went
 * out, not only from what is still blocked.
 */
export default function EmailReviewer({
  emailId,
  onActioned,
}: {
  emailId: string;
  onActioned?: (updated: EmailDetail) => void;
}) {
  const queryClient = useQueryClient();
  const emailMode = useEmailMode();
  const { user } = useAuth();
  const isAdmin = (user?.roles ?? []).includes("ADMIN");

  const detail = useQuery({
    queryKey: ["email", emailId],
    queryFn: () => api.get<EmailDetail>(`/api/v1/emails/${emailId}`),
  });

  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  // Which of the two rejections was asked for. The server's answer is the same
  // message row either way, so what happens to the prospect next — paused, or
  // already composing a replacement — is only knowable from the click.
  const [regenerating, setRegenerating] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Approve-and-keep: tick before releasing, and the sent wording is saved as
  // one of the phase's example patterns the moment the send succeeds.
  const [saveAsExample, setSaveAsExample] = useState(false);
  const [exampleSaved, setExampleSaved] = useState<PhaseExampleSummary | null>(null);
  // What the last approve/reject actually did. Held here because the controls
  // that produced it disappear the moment it succeeds, and a screen that goes
  // quiet is indistinguishable from one that failed.
  const [outcome, setOutcome] = useState<EmailDetail | null>(null);

  // Reset the editor whenever a different message is opened, so a half-typed
  // edit never leaks into the next message the operator clicks.
  useEffect(() => {
    setEditing(false);
    setRejecting(false);
    setRejectReason("");
    setRegenerating(false);
    setOutcome(null);
    setSaveAsExample(false);
    setExampleSaved(null);
  }, [emailId]);

  /**
   * Adopt the server's answer as this message's new state, then refresh
   * everything that counted on the old one.
   *
   * <p>The response body <em>is</em> the row that was just written, so it is
   * written straight into the cache rather than invalidated and re-fetched.
   * That matters most on a sandboxed deployment: nothing is delivered, so the
   * copy shown here — compliance footer included — is the only place the
   * released message can be read, and it must not flicker back to the
   * pre-approval draft while a round trip completes.
   */
  function settle(updated: EmailDetail) {
    queryClient.setQueryData(["email", emailId], updated);
    queryClient.invalidateQueries({ queryKey: ["emails"] });
    queryClient.invalidateQueries({ queryKey: ["emails-pending"] });
    queryClient.invalidateQueries({ queryKey: ["prospect"] });
    queryClient.invalidateQueries({ queryKey: ["prospect-threads"] });
    queryClient.invalidateQueries({ queryKey: ["thread-messages"] });
    onActioned?.(updated);
  }

  const save = useMutation({
    mutationFn: () => api.patch<EmailDetail>(`/api/v1/emails/${emailId}`, { subject, bodyText }),
    onSuccess: (updated) => {
      setEditing(false);
      settle(updated);
    },
  });

  const approve = useMutation({
    mutationFn: () => api.post<EmailDetail>(`/api/v1/emails/${emailId}/approve`),
    onSuccess: (updated) => {
      setOutcome(updated);
      settle(updated);
      if (saveAsExample && RECEIVED.has(updated.status)) saveExample.mutate();
    },
  });

  const saveExample = useMutation({
    mutationFn: () => api.post<PhaseExampleSummary>(`/api/v1/emails/${emailId}/example`),
    onSuccess: (s) => {
      setExampleSaved(s);
      queryClient.invalidateQueries({ queryKey: ["strategy-config"] });
      queryClient.invalidateQueries({ queryKey: ["strategy-yaml"] });
    },
  });

  /**
   * Declining, with or without asking for a replacement. Both record the
   * rejection identically; `regenerate` is what decides whether the prospect is
   * paused for a human to pick up or handed straight back to the agent with the
   * reviewer's reason as the correction.
   */
  const reject = useMutation({
    mutationFn: (regenerate: boolean) =>
      api.post<EmailDetail>(`/api/v1/emails/${emailId}/reject`, {
        reason: rejectReason,
        regenerate,
      }),
    onSuccess: (updated, regenerate) => {
      setRejecting(false);
      setRegenerating(regenerate);
      setOutcome(updated);
      settle(updated);
    },
  });

  if (detail.isLoading) return <div className="text-sm text-zinc-500">Loading message…</div>;
  if (detail.error || !detail.data) {
    return <div className="text-sm text-[var(--danger)]">Could not load this message.</div>;
  }

  const m = detail.data;
  const pending = m.direction === "OUTBOUND" && m.status === "DRAFT";
  const received = m.direction === "OUTBOUND" && RECEIVED.has(m.status);
  // Either end may have been changed by a reviewer; the agent's originals are kept for both.
  const wasEdited =
    (m.generatedBodyText != null && m.generatedBodyText !== m.bodyText) ||
    (m.generatedSubject != null && m.generatedSubject !== m.subject);

  function beginEdit() {
    setSubject(m.subject);
    setBodyText(m.bodyText);
    setEditing(true);
  }

  const actionError = approve.error ?? reject.error ?? save.error;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <EmailStatusBadge status={m.status} />
            <span className="text-xs text-[var(--fg-muted)]">
              {m.direction === "OUTBOUND" ? "Agent → prospect" : "Prospect → agent"}
              {m.phaseSlug ? ` · ${m.phaseSlug}` : ""}
            </span>
          </div>
          <h3 className="mt-1 font-medium truncate">{m.subject}</h3>
          <div className="text-xs text-[var(--fg-muted)]">
            {m.prospectEmail} · {formatDateTime(m.sentAt ?? m.receivedAt ?? m.createdAt)}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            title="Send this message to agent training"
            className="flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50 whitespace-nowrap"
          >
            <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden />
            Feedback
          </button>
          {received && isAdmin && !exampleSaved ? <SaveAsExampleControl emailId={m.id} /> : null}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            aria-label="Subject"
            className="w-full rounded-md border border-[var(--border)] p-2 text-sm"
          />
          <textarea
            rows={14}
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            aria-label="Message body"
            className="w-full rounded-md border border-[var(--border)] p-2 text-sm font-mono"
          />
          <p className="text-xs text-[var(--fg-muted)]">
            The unsubscribe footer is added automatically when the message is sent —
            you do not need to include one.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending || !subject.trim() || !bodyText.trim()}
              className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="max-h-[28rem] overflow-auto rounded-md bg-zinc-50 p-3">
          <EmailBodyView bodyText={m.bodyText} bodyHtml={m.bodyHtml} />
        </div>
      )}

      {wasEdited && !editing ? (
        <details className="rounded-md border border-[var(--border)] p-2">
          <summary className="cursor-pointer text-xs text-[var(--fg-muted)]">
            Edited by a reviewer — show what the agent originally wrote
          </summary>
          <pre className="mt-2 whitespace-pre-wrap rounded bg-zinc-50 p-2 text-xs font-sans max-h-64 overflow-auto">
            {m.generatedBodyText}
          </pre>
        </details>
      ) : null}

      {m.status === "REJECTED" && m.rejectionReason ? (
        <div className="rounded-md bg-orange-50 p-2 text-sm text-orange-900">
          <span className="font-medium">Rejected:</span> {m.rejectionReason}
        </div>
      ) : null}

      {exampleSaved ? (
        <p role="status" className="rounded-md bg-emerald-50 p-2 text-sm text-emerald-800">
          Saved as example {exampleSaved.exampleCount} for phase{" "}
          <code>{exampleSaved.phaseSlug}</code>.
        </p>
      ) : saveExample.error ? (
        <p role="alert" className="rounded-md bg-rose-50 p-2 text-sm text-rose-800">
          The message went out, but it could not be saved as an example pattern. You can still
          save it from the conversation view.
        </p>
      ) : null}

      {outcome ? (
        <ActionOutcome
          outcome={outcome}
          sandbox={!!emailMode.data?.sandbox}
          regenerating={regenerating}
        />
      ) : null}

      {actionError ? (
        <div className="rounded-md bg-rose-50 p-2 text-sm text-rose-800">
          {actionError instanceof ApiError && actionError.status === 409
            ? "This message is no longer awaiting approval — someone may have already actioned it."
            : "That action failed. Please retry."}
        </div>
      ) : null}

      {pending && !editing ? (
        <div className="space-y-2 border-t border-[var(--border)] pt-3">
          {rejecting ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium" htmlFor="reject-reason">
                Why are you rejecting this?
              </label>
              <input
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Wrong phase — this prospect already replied."
                className="w-full rounded-md border border-[var(--border)] p-2 text-sm"
              />
              <p className="text-xs text-[var(--fg-muted)]">
                Your reason is given to the agent as the correction, so write what
                should have happened instead. Rejecting on its own pauses this
                prospect until you resume it; rejecting and regenerating keeps the
                flow running and composes a replacement now.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => reject.mutate(false)}
                  disabled={reject.isPending || !rejectReason.trim()}
                  className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                >
                  {reject.isPending ? "Rejecting…" : "Confirm reject"}
                </button>
                <button
                  type="button"
                  onClick={() => reject.mutate(true)}
                  disabled={reject.isPending || !rejectReason.trim()}
                  title="Decline this draft and have the agent write another one now"
                  className="flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4" aria-hidden />
                  Reject &amp; regenerate
                </button>
                <button
                  type="button"
                  onClick={() => setRejecting(false)}
                  className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => approve.mutate()}
                disabled={approve.isPending}
                className={cn(
                  "flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50",
                  "bg-emerald-600 hover:bg-emerald-700",
                )}
              >
                <Check className="h-4 w-4" aria-hidden />
                {approve.isPending ? "Sending…" : "Approve & send"}
              </button>
              <button
                type="button"
                onClick={beginEdit}
                className="flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50"
              >
                <Pencil className="h-4 w-4" aria-hidden />
                Edit
              </button>
              <button
                type="button"
                onClick={() => setRejecting(true)}
                className="flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50"
              >
                <X className="h-4 w-4" aria-hidden />
                Reject
              </button>
              {emailMode.data?.sandbox ? (
                <span className="text-xs text-amber-800">
                  Sandbox: approving records the send without delivering anything.
                </span>
              ) : null}
              {isAdmin ? (
                <label className="flex w-full items-start gap-2 pt-1 text-xs text-[var(--fg-muted)]">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-3.5 w-3.5"
                    checked={saveAsExample}
                    onChange={(e) => setSaveAsExample(e.target.checked)}
                  />
                  <span>
                    After sending, also save this wording as an example pattern for phase{" "}
                    <code>{m.phaseSlug ?? "?"}</code>. Examples are sent with every email the
                    agent writes in that phase, so each one adds to prompt size and cost.
                  </span>
                </label>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {feedbackOpen ? (
        <EmailFeedbackDialog
          emailId={m.id}
          strategyId={m.strategyId}
          onClose={() => setFeedbackOpen(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * What became of the message the operator just actioned.
 *
 * <p>Every branch names the recipient, because "it worked" is not the same
 * claim as "it reached that address" — and a send can succeed at the API while
 * refusing to leave the building (suppressed recipient, provider refusal,
 * sandbox). Each of those is reported as what it is.
 */
function ActionOutcome({
  outcome,
  sandbox,
  regenerating,
}: {
  outcome: EmailDetail;
  sandbox: boolean;
  regenerating: boolean;
}) {
  const to = outcome.prospectEmail ?? "the prospect";
  const at = formatDateTime(outcome.sentAt ?? outcome.rejectedAt);
  const stamp = at === "—" ? "" : ` · ${at}`;

  const good = "rounded-md bg-emerald-50 p-2 text-sm text-emerald-900";
  const warn = "rounded-md bg-amber-50 p-2 text-sm text-amber-900";
  const bad = "rounded-md bg-rose-50 p-2 text-sm text-rose-800";

  switch (outcome.status) {
    case "SENT":
      return (
        <div role="status" className={good}>
          <div className="font-medium">
            {sandbox ? "Recorded as sent to " : "Sent to "}
            {to}
            {stamp}
          </div>
          {sandbox ? (
            <p className="mt-1">
              Sandbox — nothing was delivered to that address. The message exactly as it
              was released, unsubscribe footer included, is shown above.
            </p>
          ) : null}
        </div>
      );
    case "QUEUED":
      return (
        <div role="status" className={warn}>
          Accepted for {to}, but the provider has not confirmed it yet. It stays queued
          and is retried automatically.
        </div>
      );
    case "UNSUBSCRIBED":
      return (
        <div role="status" className={bad}>
          This message was not sent: {to} is on the suppression list.
        </div>
      );
    case "FAILED":
      return (
        <div role="status" className={bad}>
          The provider refused this message, so it was not sent to {to}.
          {outcome.bounceReason ? ` Reason: ${outcome.bounceReason}` : ""}
        </div>
      );
    case "REJECTED":
      // The reason is already shown above; what an operator does not otherwise
      // learn is what declining did to the conversation, which differs by which
      // button they pressed.
      return regenerating ? (
        <div role="status" className={warn}>
          Declined. The agent is composing a replacement now, with your reason as the
          correction — it appears in this thread when it is ready.
        </div>
      ) : (
        <div role="status" className={warn}>
          The prospect's flow is paused, so the agent will not immediately compose the
          same message again — resume it when you are ready.
        </div>
      );
    default:
      return null;
  }
}
